import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AlertController, LoadingController } from '@ionic/angular';
import { Observable } from 'rxjs';

import { ApiService } from 'src/services/api.service';
import { MessageService } from 'src/services/message.service';

interface TaxonomyCategory {
  category: string;
  articleCount: number;
}

interface TaxonomyCategoryArticle {
  id: string;
  title: string;
  projectId: string | null;
}

interface TaxonomyCategoryDetailed {
  category: string;
  scope: 'global' | 'project';
  projectId: string | null;
  articleCount: number;
  articles: TaxonomyCategoryArticle[];
}

interface TaxonomyCategoriesResponse {
  categories: TaxonomyCategoryDetailed[];
  meta: {
    description: string;
    lastUpdated: string;
  };
}

interface TaxonomyStats {
  tenantId: string;
  totalArticles: number;
  classifiedArticles: number;
  progressPercent: number;
  categories: TaxonomyCategory[];
  isComplete: boolean;
}

interface StrategySettings {
  strategy: string;
  confidenceThresholds: {
    llm: number;
    centroid: number;
    aggregation: number;
  };
  availableStrategies: string[];
  strategyDescriptions?: {
    [key: string]: string;
  };
}

interface TaxonomyResponse {
  taxonomyStats: TaxonomyStats;
  strategySettings: StrategySettings;
  meta: {
    description: string;
    lastUpdated: string;
  };
}

@Component({
  selector: 'app-admin-taxonomy-dashboard',
  templateUrl: './admin-taxonomy-dashboard.component.html',
  styleUrls: ['./admin-taxonomy-dashboard.component.scss'],
})
export class AdminTaxonomyDashboardComponent implements OnInit {
  stats: TaxonomyResponse | null = null;
  categories: TaxonomyCategoriesResponse | null = null;
  isLoading = true;
  isLoadingCategories = true;
  error: string | null = null;

  constructor(
    private router: Router,
    private apiService: ApiService,
    private alertController: AlertController,
    private loadingController: LoadingController,
    private messageService: MessageService,
  ) {}

  ngOnInit() {
    this.loadStats();
    this.loadCategories();
  }

  loadStats() {
    this.isLoading = true;
    this.error = null;
    this.stats = null;

    this.apiService.get<TaxonomyResponse>('/knowledge-base/taxonomy/tenant-stats').subscribe({
      next: (response) => {
        this.isLoading = false;
        this.stats = response;
      },
      error: (err) => {
        this.isLoading = false;
        this.error = 'Failed to load taxonomy stats: ' + (err.message || 'Unknown error');
      },
    });
  }

  loadCategories() {
    this.isLoadingCategories = true;

    this.apiService.get<TaxonomyCategoriesResponse>('/knowledge-base/taxonomy/categories').subscribe({
      next: (response) => {
        this.isLoadingCategories = false;
        this.categories = response;
      },
      error: (err) => {
        this.isLoadingCategories = false;
        this.error = 'Failed to load taxonomy categories: ' + (err.message || 'Unknown error');
      },
    });
  }

  onRefresh() {
    this.loadStats();
    this.loadCategories();
  }

  getStatusColor(progressPercent: number): string {
    if (progressPercent >= 90) return 'success';
    if (progressPercent >= 50) return 'warning';
    return 'danger';
  }

  getStatusText(progressPercent: number): string {
    if (progressPercent >= 90) return 'Excellent';
    if (progressPercent >= 70) return 'Good';
    if (progressPercent >= 50) return 'Moderate';
    return 'Needs Attention';
  }

  trackByCategory(index: number, category: TaxonomyCategory): string {
    return category.category;
  }

  trackByCategoryDetailed(index: number, category: TaxonomyCategoryDetailed): string {
    return `${category.category}-${category.projectId || 'global'}`;
  }

  trackByArticle(index: number, article: TaxonomyCategoryArticle): string {
    return article.id;
  }

  getArticleUrl(article: TaxonomyCategoryArticle): string {
    return `/dashboard/knowledge/articles/${article.id}/edit`;
  }

  getScopeLabel(category: TaxonomyCategoryDetailed): string {
    return category.scope === 'global' ? 'Global' : 'Project';
  }

  getScopeColor(category: TaxonomyCategoryDetailed): string {
    return category.scope === 'global' ? 'primary' : 'secondary';
  }

  async startTaxonomyOnboarding() {
    const alert = await this.alertController.create({
      header: 'Start Taxonomy Onboarding',
      message:
        'This will begin automatic classification of all knowledge base articles. This process may take several minutes to complete. Do you want to continue?',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Start Onboarding',
          role: 'confirm',
          handler: () => {
            this.performStartOnboarding();
          },
        },
      ],
    });

    await alert.present();
  }

  private async performStartOnboarding() {
    const loading = await this.loadingController.create({
      message: 'Starting taxonomy onboarding...',
    });
    await loading.present();

    try {
      await this.apiService.postPromise('/knowledge-base/taxonomy/onboarding/start', {
        skipExisting: true,
      });

      await loading.dismiss();
      this.messageService.show(
        'Taxonomy onboarding started successfully! Classification will continue in the background.',
      );
      this.loadStats(); // Refresh stats
      this.loadCategories(); // Refresh categories
    } catch (error) {
      await loading.dismiss();
      this.messageService.showError('Failed to start taxonomy onboarding: ' + (error.message || 'Unknown error'));
    }
  }

  async resetTaxonomy() {
    const alert = await this.alertController.create({
      header: 'Reset Taxonomy',
      message:
        'This will permanently delete all taxonomy classifications and categories. This action cannot be undone. Are you sure you want to proceed?',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Reset Taxonomy',
          role: 'destructive',
          handler: () => {
            this.performResetTaxonomy();
          },
        },
      ],
    });

    await alert.present();
  }

  private async performResetTaxonomy() {
    const loading = await this.loadingController.create({
      message: 'Resetting taxonomy...',
    });
    await loading.present();

    try {
      await this.apiService.postPromise('/knowledge-base/taxonomy/reset', {});

      await loading.dismiss();
      this.messageService.show('Taxonomy has been reset successfully.');
      this.loadStats(); // Refresh stats
      this.loadCategories(); // Refresh categories
    } catch (error) {
      await loading.dismiss();
      this.messageService.showError('Failed to reset taxonomy: ' + (error.message || 'Unknown error'));
    }
  }

  viewCategoryAnalysis(categoryName: string) {
    this.router.navigate(['/admin/taxonomy-dashboard/category', categoryName]);
  }

  viewArticleAnalysis(articleId: string) {
    this.router.navigate(['/admin/taxonomy-dashboard/article', articleId]);
  }

  getStrategyLabel(strategy: string): string {
    const labels: { [key: string]: string } = {
      large: 'Large (LLM)',
      'small-llm': 'Small LLM',
      'small-centroid': 'Small Centroid',
      'small-hybrid': 'Small Hybrid',
    };
    return labels[strategy] || strategy;
  }

  getStrategyColor(strategy: string): string {
    const colors: { [key: string]: string } = {
      large: 'tertiary',
      'small-llm': 'primary',
      'small-centroid': 'success',
      'small-hybrid': 'warning',
    };
    return colors[strategy] || 'medium';
  }
}
